import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Spinner, Tab, Tabs, Badge, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiArrowDownTray, HiArrowPath, HiArrowUpTray, HiPencil, HiCheck, HiXMark, HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { FaGoogleDrive } from 'react-icons/fa';
import { TbBrandOnedrive } from 'react-icons/tb';
import { renderAsync } from 'docx-preview';
import { useGetDocumentQuery, useRetryDocumentMutation, useUploadDocumentVersionMutation, useLazyGetDocumentContentQuery, useUpdateDocumentNameMutation, DOCUMENT_POLL_INTERVAL_MS } from '@/store/documentsApi';
import { DocumentStatus, MetadataApprovalStatus, ImportSource } from '@/types';
import { apiGetBlob, apiDownload } from '@/api/client';
import { useDateFormat } from '@/utils/dates';
import { DefinitionList } from '@/components/common/DefinitionList';
import { PageContent } from '@/components/layout/PageLayout';
import DetailHeader, { DetailTitle } from '@/components/layout/DetailHeader';
import DocumentStatusBadge from '@/components/documents/DocumentStatusBadge';
import DocumentSurface from '@/components/documents/DocumentSurface';
import MetadataApprovalPanel from '@/components/documents/MetadataApprovalPanel';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
  SummaryBox,
  LabelBadge,
  LabelsRow,
  PreviewFrame,
  DocxContainer,
  RtfContainer,
  CenteredSpinner,
  NameEditWrapper,
  ImportSourceIcon,
  HiddenFileInput,
  BreakAll,
} from './DocumentDetailPage.styled';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  // Poll only while this document is still processing, so the detail view reflects backend
  // status transitions (parse → label → embed → ready) that happen out of band on Service Bus
  // workers. selectFromResult derives isProcessing inside the subscription that already drives
  // the UI; once the document is Ready/Failed the interval drops to 0 (polling off). This is a
  // single-row fetch by id, so each poll is minimal. A second subscription with identical args
  // (`id`) shares the same cache entry, so it only attaches the polling timer — no extra request.
  const { data: doc, isLoading, isProcessing } = useGetDocumentQuery(id!, {
    skip: !id,
    selectFromResult: (result) => ({
      data: result.data,
      isLoading: result.isLoading,
      isProcessing:
        result.data != null &&
        result.data.status !== DocumentStatus.Ready &&
        result.data.status !== DocumentStatus.Failed,
    }),
  });
  useGetDocumentQuery(id!, {
    skip: !id || !isProcessing,
    pollingInterval: DOCUMENT_POLL_INTERVAL_MS,
  });
  const [retryDocument] = useRetryDocumentMutation();
  const [uploadVersion] = useUploadDocumentVersionMutation();
  const [updateDocumentName] = useUpdateDocumentNameMutation();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [fetchContent, { data: contentData }] = useLazyGetDocumentContentQuery();
  const versionInputRef = useRef<HTMLInputElement>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [rtfHtml, setRtfHtml] = useState<string | null>(null);
  const [docxBlob, setDocxBlob] = useState<Blob | null>(null);
  const [docxRendered, setDocxRendered] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');

  const isMobile = window.innerWidth <= 768;

  const docxRef = useCallback((node: HTMLDivElement | null) => {
    if (!node || !docxBlob) return;
    node.innerHTML = '';
    renderAsync(docxBlob, node, undefined, {
      inWrapper: true,
      ignoreWidth: isMobile,
      ignoreHeight: true,
    })
      .then(() => setDocxRendered(true))
      .catch(() => {
        setDocxBlob(null);
        setActiveTab('text');
      });
  }, [docxBlob, isMobile]);

  useEffect(() => {
    if (!doc) return;

    setPdfUrl(null);
    setRtfHtml(null);
    setDocxBlob(null);
    setDocxRendered(false);

    const isPdf = doc.documentType === 'Pdf';
    const isDocx = doc.documentType === 'Docx';
    setActiveTab(isPdf || isDocx ? 'preview' : 'text');

    if (doc.status === DocumentStatus.Failed) return;

    if (isPdf) {
      setLoadingPreview(true);
      apiGetBlob(`/documents/${doc.id}/download`)
        .then((blob) => setPdfUrl(URL.createObjectURL(blob)))
        .catch(() => {})
        .finally(() => setLoadingPreview(false));
    }

    if (isDocx) {
      setLoadingPreview(true);
      apiGetBlob(`/documents/${doc.id}/download`)
        .then((blob) => setDocxBlob(blob))
        .catch(() => {})
        .finally(() => setLoadingPreview(false));
    }

    if (doc.documentType === 'Rtf') {
      setLoadingPreview(true);
      apiGetBlob(`/documents/${doc.id}/download`)
        .then(async (blob) => {
          const arrayBuffer = await blob.arrayBuffer();
          const { RTFJS } = await import('rtf.js');
          const rtfDoc = new RTFJS.Document(arrayBuffer, {});
          const htmlElements = await rtfDoc.render();
          const container = document.createElement('div');
          htmlElements.forEach((el) => container.appendChild(el));
          setRtfHtml(container.innerHTML);
          setActiveTab('preview');
        })
        .catch(() => {
          setActiveTab('text');
        })
        .finally(() => setLoadingPreview(false));
    }

    if (doc.status === DocumentStatus.Ready) {
      fetchContent(doc.id);
    }

    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.status]);

  if (isLoading || !doc) {
    return (
      <PageContent>
        <LoadingSpinner />
      </PageContent>
    );
  }

  const isPdf = doc.documentType === 'Pdf';
  const isDocx = doc.documentType === 'Docx';
  const isRtf = doc.documentType === 'Rtf';
  const extractedText = contentData?.extractedText ?? null;
  const hasDocumentPreview = isPdf || isDocx || (isRtf && rtfHtml) || loadingPreview;

  return (
    <PageContent>
      <DetailHeader
        onBack={() => navigate(-1)}
        title={
          isEditingName ? (
            <NameEditWrapper>
              <Form.Control
                size="sm"
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateDocumentName({ id: doc.id, documentName: editedName || null });
                    setIsEditingName(false);
                  }
                  if (e.key === 'Escape') setIsEditingName(false);
                }}
                autoFocus
              />
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  updateDocumentName({ id: doc.id, documentName: editedName || null });
                  setIsEditingName(false);
                }}
              >
                <HiCheck size="0.875rem" />
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setIsEditingName(false)}>
                <HiXMark size="0.875rem" />
              </Button>
            </NameEditWrapper>
          ) : (
            <div className="d-flex align-items-center gap-2">
              {doc.importSource === ImportSource.GoogleDrive && (
                <ImportSourceIcon><FaGoogleDrive size="0.875rem" /></ImportSourceIcon>
              )}
              {doc.importSource === ImportSource.OneDrive && (
                <ImportSourceIcon><TbBrandOnedrive size="0.875rem" /></ImportSourceIcon>
              )}
              <DetailTitle>{doc.documentName ?? doc.fileName}</DetailTitle>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-muted"
                onClick={() => {
                  setEditedName(doc.documentName ?? doc.fileName);
                  setIsEditingName(true);
                }}
                title={t('documents.editName')}
              >
                <HiPencil size="0.875rem" />
              </Button>
            </div>
          )
        }
        meta={
          <>
            <DocumentStatusBadge status={doc.status} processingProgress={doc.processingProgress} showProgress />
            <span>{doc.documentType}</span>
            <span>{formatFileSize(doc.fileSizeBytes)}</span>
            {doc.pageCount != null && <span>{doc.pageCount} {t('documents.table.pages').toLowerCase()}</span>}
            <span>{formatDateTime(doc.createdAt)}</span>
          </>
        }
        actions={
          <>
            {doc.externalFileUrl && (
              <Button
                variant="outline-secondary"
                size="sm"
                as="a"
                href={doc.externalFileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <HiArrowTopRightOnSquare size="0.875rem" className="me-1" />
                {doc.importSource === ImportSource.GoogleDrive
                  ? t('import.openInGoogleDrive')
                  : t('import.openInOneDrive')}
              </Button>
            )}
            {doc.status === DocumentStatus.Failed && (
              <Button variant="outline-warning" size="sm" onClick={() => retryDocument(doc.id)}>
                <HiArrowPath size="0.875rem" className="me-1" />
                {t('documents.retry')}
              </Button>
            )}
            {doc.status !== DocumentStatus.Failed && (
              <>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => apiDownload(`/documents/${doc.id}/download`, doc.fileName)}
                >
                  <HiArrowDownTray size="0.875rem" className="me-1" />
                  {t('documents.download')}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => versionInputRef.current?.click()}
                >
                  <HiArrowUpTray size="0.875rem" className="me-1" />
                  {t('metadata.uploadNewVersion')}
                </Button>
                <HiddenFileInput
                  ref={versionInputRef}
                  type="file"
                  accept=".pdf,.docx,.rtf,.jpg,.jpeg,.png"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      await uploadVersion({ parentId: doc.id, file, fileName: file.name });
                      e.target.value = '';
                    }
                  }}
                />
              </>
            )}
          </>
        }
      />

      {doc.labels?.length > 0 && (
        <LabelsRow>
          {doc.labels.map((label) => (
            <LabelBadge key={label.id}>{label.name}</LabelBadge>
          ))}
        </LabelsRow>
      )}

      {/* Document info table — always visible */}
      <DefinitionList
        items={[
          {
            label: t('documents.table.status'),
            value: <DocumentStatusBadge status={doc.status} processingProgress={doc.processingProgress} showProgress />,
            hideIfEmpty: false,
          },
          {
            label: t('documents.table.name'),
            value: <BreakAll>{doc.fileName}</BreakAll>,
            hideIfEmpty: false,
          },
          { label: t('documents.detail.type'), value: doc.documentType, hideIfEmpty: false },
          { label: t('documents.table.size'), value: formatFileSize(doc.fileSizeBytes), hideIfEmpty: false },
          { label: t('documents.table.pages'), value: doc.pageCount },
          { label: t('documents.table.uploaded'), value: formatDateTime(doc.createdAt), hideIfEmpty: false },
          { label: t('documents.detail.processedAt'), value: doc.processedAt ? formatDateTime(doc.processedAt) : null },
          {
            label: t('metadata.sprawa'),
            value: doc.sprawaId
              ? <Link to={`/sprawy/${doc.sprawaId}`}>{doc.sprawaSignature ?? doc.sprawaId}</Link>
              : null,
          },
          {
            label: t('metadata.version'),
            value: doc.version > 1 ? (
              <>
                {doc.version}
                {doc.previousVersionId && (
                  <> (<Link to={`/documents/${doc.previousVersionId}`}>{t('metadata.version')} {doc.version - 1}</Link>)</>
                )}
              </>
            ) : null,
          },
          {
            label: t('documents.detail.metadata'),
            value: doc.metadataApprovalStatus !== MetadataApprovalStatus.None ? (
              <Badge bg={
                doc.metadataApprovalStatus === MetadataApprovalStatus.Approved ? 'success' :
                doc.metadataApprovalStatus === MetadataApprovalStatus.Rejected ? 'danger' : 'warning'
              }>
                {t(`metadata.${doc.metadataApprovalStatus.toLowerCase()}`)}
              </Badge>
            ) : null,
          },
        ]}
      />

      <MetadataApprovalPanel document={doc} />

      {doc.summary && (
        <SummaryBox>{doc.summary}</SummaryBox>
      )}

      {(hasDocumentPreview || extractedText) && (
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'preview')} className="mb-3">
          {hasDocumentPreview && (
            <Tab eventKey="preview" title={t('documents.preview.document', 'Document')}>
              {loadingPreview ? (
                <CenteredSpinner><Spinner animation="border" /></CenteredSpinner>
              ) : isPdf && pdfUrl ? (
                <PreviewFrame src={pdfUrl} title={doc.fileName} />
              ) : isDocx && docxBlob ? (
                <>
                  {!docxRendered && <CenteredSpinner><Spinner animation="border" /></CenteredSpinner>}
                  <DocxContainer ref={docxRef} style={docxRendered ? undefined : { display: 'none' }} />
                </>
              ) : isRtf && rtfHtml ? (
                <RtfContainer dangerouslySetInnerHTML={{ __html: rtfHtml }} />
              ) : null}
            </Tab>
          )}
          {extractedText && (
            <Tab eventKey="text" title={t('documents.preview.extractedText', 'Extracted Text')}>
              <DocumentSurface
                title={doc.documentName ?? doc.fileName}
                meta={{
                  sygnatura: doc.sprawaSignature,
                  data: formatDateTime(doc.createdAt),
                }}
              >
                {extractedText}
              </DocumentSurface>
            </Tab>
          )}
        </Tabs>
      )}
    </PageContent>
  );
}
